function initCartUpsells() {
  class CartUpsells extends HTMLElement {
    static MOBILE_BREAKPOINT = 640;

    static selectors = {
      upsellContent: '[data-upsell-content]',
      toggleButton: '[data-toggle-button]',
      mobileArrowLeft: '[data-mobile-arrow-left]',
      mobileArrowRight: '[data-mobile-arrow-right]',
      topCarouselArrow: '[data-top-carousel-arrow]',
      bottomCarouselArrow: '[data-bottom-carousel-arrow]',
    }

    static classes = {
      hidden: 'hidden',
      disabled: 'disabled',
      cartUpsellsToggleButtonExpanded: 'cart-upsells__toggle-button--expanded',
      cartUpsellsToggleButtonCollapsed: 'cart-upsells__toggle-button--collapsed',
      cartUpsellsAccordionCollapsed: 'cart-upsells__accordion--collapsed',
    }

    constructor() {
      super();
      this.settings = {};
      this.cartDrawer = null;
      this.cartData = null;
      this.upsellsData = null;
      this.upsellProductId = null;
      this.upsellProductLimit = 6;
      this.upsellIntent = 'related';
      this.originalParent = null;
      this.isExpanded = true;
      this.accordionHeight = null;
      this.upsellContent = null;
      this.toggleButton = null;
      this.leftCarouselArrowButton = null;
      this.rightCarouselArrowButton = null;
      this.topCarouselArrowButton = null;
      this.bottomCarouselArrowButton = null;
      this.init();
    }

    async init() {
      this.cartDrawer = document.getElementById('cart-drawer');
      if (this.cartDrawer) {
        this.settings = JSON.parse(this.getAttribute('data-settings'));

        if (!this.settings.enable_cart_upsells) return;

        this.upsellProductLimit = this.settings.cart_upsells_product_limit;
        this.upsellIntent = this.settings.cart_upsells_intent;

        this.originalParent = this.parentElement;
        this.upsellContent = this.querySelector(CartUpsells.selectors.upsellContent);
        this.toggleButton = this.querySelector(CartUpsells.selectors.toggleButton);
        this.leftCarouselArrowButton = this.querySelector(CartUpsells.selectors.mobileArrowLeft);
        this.rightCarouselArrowButton = this.querySelector(CartUpsells.selectors.mobileArrowRight);
        this.topCarouselArrowButton = this.querySelector(CartUpsells.selectors.topCarouselArrow);
        this.bottomCarouselArrowButton = this.querySelector(CartUpsells.selectors.bottomCarouselArrow);
        this.mobileMediaQuery = window.matchMedia(`(max-width: ${CartUpsells.MOBILE_BREAKPOINT}px)`);

        await this.loadInitialCartData();
        await this.getUpsellsData();
        this.renderUpsells();
        this.initAccordionToggle();
        this.initResizeObserver();
        this.setupCarouselScrollListeners();
        // Listen for cart drawer open events
        this.cartDrawer.addEventListener('dialog:after-show', () => {
          this.onCartDrawerOpen();
        });
      }
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    async loadInitialCartData() {
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`);
        this.cartData = await response.json();
      } catch (error) {
        console.error('Failed to load initial cart:', error);
      }
    }

    async getUpsellsData() {
      const startTime = performance.now();
      if (!this.cartData?.items?.length) return;
      // Sort cart items by price descending to get most expensive first
      const nonFreeCartItems = this.cartData.items.filter(item => item.price > 0);
      const sortedCartItems = nonFreeCartItems.sort((a, b) => b.price - a.price);
      let attemptCount = 0;
      const maxAttempts = 3;
      this.upsellsData = { products: [] };

      while (attemptCount < maxAttempts && this.upsellsData.products.length < 6) {
        // Get the product ID for this attempt (most expensive, then next most expensive, etc.)
        const currentProductId = sortedCartItems[attemptCount]?.product_id;

        if (!currentProductId) break; // No more cart items to try

        try {
          const response = await fetch(
            `/recommendations/products.json?product_id=${currentProductId}&intent=${this.upsellIntent}&limit=${this.upsellProductLimit}`
          );
          const currentUpsellsData = await response.json();

          if (currentUpsellsData?.products?.length) {
            // Filter out products that are already in our upsells array to avoid duplicates
            const existingProductIds = new Set(this.upsellsData.products.map(p => p.id));
            const newProducts = currentUpsellsData.products.filter(p => !existingProductIds.has(p.id));

            // Append new products to existing ones
            this.upsellsData.products.push(...newProducts);

            // Trim to maximum limit if we exceed it
            if (this.upsellsData.products.length > this.upsellProductLimit) {
              this.upsellsData.products = this.upsellsData.products.slice(0, this.upsellProductLimit);
            }
          }

          attemptCount++;

          // If intent is not 'complementary', only do one attempt
          if (this.upsellIntent !== 'complementary') {
            break;
          }

        } catch (error) {
          console.error(`Failed to load upsells data on attempt ${attemptCount + 1}:`, error);
          attemptCount++;
        }
      }

      const endTime = performance.now();
      console.log(`Time taken to load upsells data: ${endTime - startTime} milliseconds`);
      console.log(`Loaded ${this.upsellsData.products.length} upsell products after ${attemptCount} attempts`);
    }

    async updateUpsells() {
      if (!this.cartData) return;
      await this.getUpsellsData();
    }

    toggleVerticalScrollOverlay() {
      // Check if content overflows
      const hasOverflow = this.upsellContent.scrollHeight > this.upsellContent.clientHeight;
      const classAction = hasOverflow ? 'remove' : 'add';

      this.topCarouselArrowButton.classList[classAction](CartUpsells.classes.hidden);
      this.bottomCarouselArrowButton.classList[classAction](CartUpsells.classes.hidden);
    }

    renderUpsells() {
      if (!this.upsellsData?.products?.length) {
        this.classList.add(CartUpsells.classes.hidden);
        return;
      }
      if (this.upsellContent) {
        // Clear existing content
        this.upsellContent.innerHTML = '';

        // Track image loading promises to prevent layout shift
        const imageLoadPromises = [];

        this.upsellsData.products.forEach((product) => {
          // Create the product card container
          const productCard = document.createElement('div');
          productCard.className = 'cart-upsells__product-card';

          // Format price (Shopify prices are in cents)
          const formattedPrice = window.formatMoney ?
            window.formatMoney(product.price) :
            `$${(product.price / 100).toFixed(2)}`;

          // Create image element and preload it
          const img = new Image();
          img.src = product.featured_image;
          img.alt = product.title;
          img.className = 'cart-upsells__product-image';

          // Add loading promise to track when image is ready
          const imagePromise = new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve; // Resolve even on error to prevent hanging
          });
          imageLoadPromises.push(imagePromise);

          productCard.innerHTML = `
            <a href="${product.url}">
              <div class="cart-upsells__product-image">
              </div>
              <div class="cart-upsells__product-details">
                <div class="cart-upsells__product-title h6">${product.title}</div>
                <div class="cart-upsells__product-price h6">${formattedPrice}</div>
              </div>
            </a>
            <div class="cart-upsells__actions">
              ${this.createQuickBuyButton(product)}
            </div>
          `;

          // Insert the preloaded image
          const imageContainer = productCard.querySelector('.cart-upsells__product-image');
          imageContainer.appendChild(img);

          this.upsellContent.appendChild(productCard);
        });

        this.initializeQuickBuyModals();

        // Wait for all images to load before showing and removing transform
        Promise.all(imageLoadPromises).then(() => {
          // Remove the transform that's hiding the drawer
          const drawerBody = this.cartDrawer?.getShadowPartByName('body');
          if (drawerBody) {
            drawerBody.style.removeProperty('transform');
          }

          // Now that images are loaded, set up the UI
          setTimeout(() => {
            this.toggleVerticalScrollOverlay();
          }, 50);
        });
      }
    }

    createQuickBuyButton(product) {
      const modalId = `modal-cart-upsells-${product.id}`;

      if (product.variants.length === 1 && !product.selling_plan_groups?.length) {
        // Single variant product - direct add to cart
        return `
          <form class="product-form" data-product-id="${product.id}">
            <input type="hidden" name="id" value="${product.variants[0].id}">
            <button type="submit" class="cart-upsells__quick-add-button">
              Add to Cart
            </button>
          </form>
        `;
      } else {
        // Multiple variants - need quick buy modal
        return `
          <button
            type="button"
            aria-controls="${modalId}"
            class="cart-upsells__quick-add-button"
            data-product-handle="${product.handle}"
          >
            OPTIONS
          </button>
          <quick-buy-modal
            handle="${product.handle}"
            class="quick-buy-modal modal"
            id="${modalId}"
          >
          </quick-buy-modal>
        `;
      }
    }

    initializeQuickBuyModals() {
      // Get all quick-buy-modal elements in the upsells area
      const quickBuyModals = this.querySelectorAll('quick-buy-modal');

      quickBuyModals.forEach((modal) => {
        // Make sure the custom element is properly connected
        if (!modal.connectedCallback) {
          // Re-register the element if needed
          customElements.upgrade(modal);
        }
      });

      // Handle direct add to cart forms
      const productForms = this.querySelectorAll('.product-form');
      productForms.forEach((form) => {
        form.addEventListener('submit', this.handleDirectAddToCart.bind(this));
      });

      // Set up click handlers for quick buy buttons
      const quickBuyButtons = this.querySelectorAll('[aria-controls^="modal-cart-upsells-"]');
      quickBuyButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const modalId = button.getAttribute('aria-controls');
          const modal = document.getElementById(modalId);
          if (modal && modal.show) {
            modal.show();
          }
        });
      });
    }

    async handleDirectAddToCart(event) {
      event.preventDefault();
      const form = event.target;
      const formData = new FormData(form);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          // Trigger cart update event
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } else {
          console.error('Failed to add product to cart');
        }
      } catch (error) {
        console.error('Error adding product to cart:', error);
      }
    }

    onCartDrawerOpen() {
      if (!this.upsellsData?.products?.length) return;
      this.accordionHeight = this.upsellContent.offsetHeight;
      this.upsellContent.style.height = this.accordionHeight + 'px';
      this.toggleVerticalScrollOverlay();
      this.initialiseCarouselButtons();
    }

    initResizeObserver() {
      if (!this.upsellsData?.products?.length) return;

      // Listen for media query changes
      this.mobileMediaQuery.addEventListener('change', () => {
        this.updateAccordionBehavior();
      });
    }

    initAccordionToggle() {
      if (!this.upsellsData?.products?.length) return;

      if (this.toggleButton && this.upsellContent) {
        // Check if mobile and apply accordion behavior
        this.updateAccordionBehavior();

        this.toggleButton.addEventListener('click', () => {
          // Only toggle if we're on mobile
          if (this.mobileMediaQuery.matches) {
            this.toggleAccordion();
          }
        });
      }
    }

    updateAccordionBehavior() {

      if (!this.upsellContent || !this.toggleButton) return;
      this.upsellContent.style.height = 'auto';
      if (this.mobileMediaQuery.matches) {
        this.toggleButton.classList.add(CartUpsells.classes.cartUpsellsToggleButtonExpanded);
        setTimeout(() => {
          this.accordionHeight = this.upsellContent.offsetHeight;
        }, 300);
      } else {
        // Desktop: Disable accordion behavior
        this.classList.remove(CartUpsells.classes.cartUpsellsAccordionCollapsed);

        // Reset icons
        if (this.toggleButton) {
          this.toggleButton.classList.add(CartUpsells.classes.cartUpsellsToggleButtonExpanded);
          this.toggleButton.classList.remove(CartUpsells.classes.cartUpsellsToggleButtonCollapsed);
        }
      }
    }

    collapseAccordion() {
      this.isExpanded = false;
      this.upsellContent.style.height = '0';
      this.classList.add(CartUpsells.classes.cartUpsellsAccordionCollapsed);
      this.toggleButton.classList.add(CartUpsells.classes.cartUpsellsToggleButtonCollapsed);
      this.toggleButton.classList.remove(CartUpsells.classes.cartUpsellsToggleButtonExpanded);
      this.upsellContent.setAttribute('aria-expanded', 'false');
    }

    expandAccordion() {
      this.isExpanded = true;
      this.upsellContent.style.height = this.accordionHeight + 'px';
      this.classList.remove(CartUpsells.classes.cartUpsellsAccordionCollapsed);
      this.toggleButton.classList.add(CartUpsells.classes.cartUpsellsToggleButtonExpanded);
      this.toggleButton.classList.remove(CartUpsells.classes.cartUpsellsToggleButtonCollapsed);
      this.upsellContent.setAttribute('aria-expanded', 'true');
    }

    toggleAccordion() {

      if (!this.mobileMediaQuery.matches) return;

      this.isExpanded = !this.isExpanded;

      if (this.isExpanded) {
        this.expandAccordion();
      } else {
        this.collapseAccordion();
      }
    }

    initialiseCarouselButtons() {
      this.initialiseMobileCarouselButtons();
      this.initialiseDesktopCarouselButtons();
      this.setupCarouselScrollListeners();
    }

    initialiseMobileCarouselButtons() {
      const productCards = this.querySelectorAll('.cart-upsells__product-card');
      const productCardWidth = productCards[0].offsetWidth;

      if (this.leftCarouselArrowButton && this.rightCarouselArrowButton) {
        this.leftCarouselArrowButton.addEventListener('click', () => {
          this.upsellContent.scroll({
            left: this.upsellContent.scrollLeft - productCardWidth,
            behavior: 'smooth',
          });
        });

        this.rightCarouselArrowButton.addEventListener('click', () => {
          this.upsellContent.scroll({
            left: this.upsellContent.scrollLeft + productCardWidth,
            behavior: 'smooth',
          });
        });
      }
    }

    initialiseDesktopCarouselButtons() {
      const productCards = this.querySelectorAll('.cart-upsells__product-card');
      const productCardHeight = productCards[0].offsetHeight;

      if (this.topCarouselArrowButton && this.bottomCarouselArrowButton) {
          this.topCarouselArrowButton.addEventListener('click', () => {
            this.upsellContent.scroll({
              top: this.upsellContent.scrollTop - productCardHeight,
              behavior: 'smooth',
            });
          });

          this.bottomCarouselArrowButton.addEventListener('click', () => {
            this.upsellContent.scroll({
              top: this.upsellContent.scrollTop + productCardHeight,
              behavior: 'smooth',
            });
          });
      }
    }

    setupCarouselScrollListeners() {
      if (!this.upsellContent) return;

      this.upsellContent.removeEventListener('scroll', this.debouncedScrollHandler);

      this.debouncedScrollHandler = this.debounce(() => {
        if (this.leftCarouselArrowButton && this.rightCarouselArrowButton) {
          this.updateMobileCarouselButtonStates();
        }
        if (this.topCarouselArrowButton && this.bottomCarouselArrowButton) {
          this.updateDesktopCarouselButtonStates();
        }
      }, 16);

      this.upsellContent.addEventListener('scroll', this.debouncedScrollHandler);

      // Set initial button states
      if (this.leftCarouselArrowButton && this.rightCarouselArrowButton) {
        this.updateMobileCarouselButtonStates();
      }
      if (this.topCarouselArrowButton && this.bottomCarouselArrowButton) {
        this.updateDesktopCarouselButtonStates();
      }
    }

    updateMobileCarouselButtonStates() {
      if (!this.leftCarouselArrowButton || !this.rightCarouselArrowButton) return;

      const scrollLeft = this.upsellContent.scrollLeft;
      const offsetWidth = this.upsellContent.offsetWidth;
      const productCards = this.querySelectorAll('.cart-upsells__product-card');
      const productCardWidth = productCards[0].offsetWidth;
      const maxScroll = scrollLeft >= offsetWidth + productCardWidth;


      // Update left button state
      if (scrollLeft <= 0) {
        this.leftCarouselArrowButton.classList.add(CartUpsells.classes.disabled);
      } else {
        this.leftCarouselArrowButton.classList.remove(CartUpsells.classes.disabled);
      }

      // Update right button state
      if (maxScroll) {
        this.rightCarouselArrowButton.classList.add(CartUpsells.classes.disabled);
      } else {
        this.rightCarouselArrowButton.classList.remove(CartUpsells.classes.disabled);
      }
    }

    updateDesktopCarouselButtonStates() {
      if (!this.topCarouselArrowButton || !this.bottomCarouselArrowButton) return;

      const scrollTop = this.upsellContent.scrollTop;
      const scrollHeight = this.upsellContent.scrollHeight;
      const clientHeight = this.upsellContent.clientHeight;
      const maxScrollTop = scrollHeight - clientHeight;

      // Update top button state
      if (scrollTop <= 12) {
        this.topCarouselArrowButton.classList.add(CartUpsells.classes.disabled);
      } else {
        this.topCarouselArrowButton.classList.remove(CartUpsells.classes.disabled);
      }

      // Update bottom button state
      if (scrollTop >= maxScrollTop) {
        this.bottomCarouselArrowButton.classList.add(CartUpsells.classes.disabled);
      } else {
        this.bottomCarouselArrowButton.classList.remove(CartUpsells.classes.disabled);
      }
    }
  }

  // Register the custom element
  if (!customElements.get('cart-upsells')) {
    customElements.define('cart-upsells', CartUpsells);
  }
}

initCartUpsells();